import React, { Suspense, lazy, useEffect, useState } from 'react';
import Link from '../../screens/detail-pages/Link';
import { v2 } from '../../screens/account/api';
import {
    RESTAURANT_UPDATES,
    sized,
    timeAgo,
    type FeedPost,
    type RestaurantStory,
} from './feed';

/**
 * "Updates from {restaurant}" on the restaurant page (mock "restaurant-updates"): its live
 * stories, then its posts, newest first. Public, loaded in the browser after the page
 * renders, so the server render (and SEO) is unchanged; nothing shows when the restaurant
 * has never posted. The story viewer is a separate chunk, fetched only when there are
 * stories. Only with ENABLE_FEED.
 */

const StoryTiles = lazy(() =>
    import(
        /* webpackChunkName: "StoryViewer" */ '../../screens/feed/stories'
    ).then(m => ({ default: m.StoryTiles })),
);

interface Updates {
    stories: RestaurantStory[];
    posts: { posts: FeedPost[]; nextCursor: string | null };
}

const PostRow = ({ post }: { post: FeedPost }) => (
    <li className="flex flex-col gap-2 border-t border-border-subtle py-5">
        <time dateTime={post.createdAt} className="text-caption text-ink-muted">
            {timeAgo(post.createdAt)}
        </time>
        {post.content && (
            <p className="text-body-1 break-words whitespace-pre-line text-ink">
                {post.content}
            </p>
        )}
        {post.images.length > 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {post.images.map(img => (
                    <img
                        key={img.templatedUrl}
                        src={sized(img, '400x400-fit=cover')}
                        alt=""
                        loading="lazy"
                        className="aspect-square w-full rounded-lg object-cover"
                    />
                ))}
            </div>
        )}
        {post.item?.name &&
            (post.item.path ? (
                <Link
                    to={post.item.path}
                    className="self-start text-body-2 font-medium text-link no-underline hover:underline"
                >
                    {post.item.name}
                    {post.item.price && ` · ${post.item.price.display}`}
                </Link>
            ) : (
                <span className="text-body-2 text-ink-muted">
                    {post.item.name}
                </span>
            ))}
    </li>
);

export const RestaurantUpdates = ({
    legacyRestaurantId,
    restaurantName,
}: {
    legacyRestaurantId: string;
    restaurantName: string;
}) => {
    const [updates, setUpdates] = useState<Updates | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        let cancelled = false;
        v2<{ restaurantUpdates: Updates }>(RESTAURANT_UPDATES, {
            id: legacyRestaurantId,
        })
            .then(d => !cancelled && setUpdates(d.restaurantUpdates))
            // A section that doesn't load just doesn't show.
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [legacyRestaurantId]);

    if (!updates || (!updates.stories.length && !updates.posts.posts.length))
        return null;

    const more = async () => {
        if (!updates.posts.nextCursor) return;
        setLoadingMore(true);
        try {
            const d = await v2<{ restaurantUpdates: Updates }>(
                RESTAURANT_UPDATES,
                { id: legacyRestaurantId, before: updates.posts.nextCursor },
            );
            setUpdates(
                prev =>
                    prev && {
                        ...prev,
                        posts: {
                            posts: [
                                ...prev.posts.posts,
                                ...d.restaurantUpdates.posts.posts,
                            ],
                            nextCursor: d.restaurantUpdates.posts.nextCursor,
                        },
                    },
            );
        } catch {
            // The button stays for another try.
        } finally {
            setLoadingMore(false);
        }
    };

    const restaurant = updates.stories[0]?.restaurant;
    return (
        <section aria-labelledby="updates-heading" className="tw mt-10">
            <h2
                id="updates-heading"
                className="mb-4 text-title-2 font-semibold text-ink-strong"
            >
                Updates from {restaurantName}
            </h2>
            {restaurant && (
                <div className="mb-4">
                    <Suspense fallback={null}>
                        <StoryTiles
                            groups={[{ restaurant, stories: updates.stories }]}
                        />
                    </Suspense>
                </div>
            )}
            {updates.posts.posts.length > 0 && (
                <ul>
                    {updates.posts.posts.map(post => (
                        <PostRow key={post.id} post={post} />
                    ))}
                </ul>
            )}
            {updates.posts.nextCursor && (
                <button
                    type="button"
                    onClick={more}
                    disabled={loadingMore}
                    className="mt-2 text-body-2 font-medium text-link hover:underline disabled:opacity-60"
                >
                    {loadingMore ? 'Loading…' : 'Show older updates'}
                </button>
            )}
        </section>
    );
};
